<div class="flex h-full min-h-0 w-full flex-col overflow-hidden">
  <header class="shrink-0 border-b border-zinc-200 pb-4 dark:border-zinc-800">
    <div class="flex flex-wrap items-start justify-between gap-4">
      <div class="min-w-0">
        <h2 class="m-0 text-lg font-semibold text-zinc-900 dark:text-zinc-100">Creatives</h2>
        <p class="m-0 mt-1 text-sm text-zinc-500 dark:text-zinc-400">
          Brand asset library for internal use only. Loaded live from S3.
        </p>
      </div>

      <div class="flex flex-wrap items-end gap-3">
        <button
          type="button"
          class="rounded-lg border border-zinc-300 bg-white px-2.5 py-1.5 text-xs font-medium text-zinc-800 hover:bg-zinc-50 disabled:opacity-50 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100 dark:hover:bg-zinc-800"
          [disabled]="loadingState() === 'loading'"
          (click)="loadLibrary()"
        >
          Refresh
        </button>

        <div class="flex flex-col items-start gap-1.5 sm:items-end">
          <p class="m-0 text-xs font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
            All cards background
          </p>
          <div
            class="inline-flex rounded-lg border border-zinc-200 bg-zinc-50 p-0.5 dark:border-zinc-700 dark:bg-zinc-900"
            role="group"
            aria-label="Set background for all cards"
          >
            @for (mode of backgroundModes; track mode) {
            <button
              type="button"
              class="rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors"
              [class]="
                isGlobalBackgroundActive(mode)
                  ? 'bg-white text-zinc-900 shadow-sm dark:bg-zinc-800 dark:text-zinc-100'
                  : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100'
              "
              (click)="setGlobalBackground(mode)"
            >
              {{ backgroundModeLabel(mode) }}
            </button>
            }
          </div>
        </div>
      </div>
    </div>

    @if (loadingState() === 'loaded') {
    <div class="mt-4 flex flex-wrap gap-2" role="tablist" aria-label="Brand asset groups">
      @for (tab of tabs(); track tab.id) {
      <button
        type="button"
        role="tab"
        class="rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors"
        [class]="getTabClass(tab.id)"
        [attr.aria-selected]="isTabSelected(tab.id)"
        (click)="selectTab(tab.id)"
      >
        {{ tabLabel(tab) }}
      </button>
      }
    </div>
    }

    <section
      class="mt-4 rounded-xl border border-zinc-200 bg-zinc-50 p-3 dark:border-zinc-800 dark:bg-zinc-900/40"
      aria-label="Upload creative"
    >
      <div class="flex flex-wrap items-end gap-3">
        <div class="min-w-[10rem] space-y-1">
          <label for="creatives-upload-folder" class="block text-xs font-medium text-zinc-600 dark:text-zinc-300"
            >Destination folder</label
          >
          <select
            id="creatives-upload-folder"
            class="block w-full rounded-lg border border-zinc-300 bg-white px-2.5 py-1.5 text-sm text-zinc-900 dark:border-zinc-600 dark:bg-zinc-950 dark:text-zinc-100"
            [value]="uploadFolder()"
            (change)="onUploadFolderChange($any($event.target).value)"
          >
            @for (folder of uploadFolders; track folder.prefix) {
            <option [value]="folder.prefix">{{ folder.label }} ({{ folder.prefix }})</option>
            }
          </select>
        </div>

        <div class="min-w-[12rem] flex-1 space-y-1">
          <label for="creatives-upload-file" class="block text-xs font-medium text-zinc-600 dark:text-zinc-300"
            >Image or video file</label
          >
          <input
            id="creatives-upload-file"
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif,video/mp4,video/webm,video/quicktime,.png,.jpg,.jpeg,.svg,.webp,.gif,.mp4,.webm,.mov,.m4v"
            class="block w-full text-sm text-zinc-700 file:mr-3 file:rounded-md file:border-0 file:bg-zinc-200 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-zinc-800 dark:text-zinc-300 dark:file:bg-zinc-800 dark:file:text-zinc-100"
            (change)="onFileSelected($event)"
          />
        </div>

        <button
          type="button"
          class="rounded-lg border border-zinc-900 bg-zinc-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-zinc-800 disabled:opacity-50 dark:border-zinc-100 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white"
          [disabled]="uploadState() === 'uploading' || !uploadFileName()"
          (click)="uploadSelectedFile()"
        >
          {{ uploadState() === 'uploading' ? 'Uploading…' : 'Upload' }}
        </button>
      </div>

      <label class="sr-only" for="creatives-replace-file">Replacement file</label>
      <input
        #replaceFileInput
        id="creatives-replace-file"
        type="file"
        accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif,video/mp4,video/webm,video/quicktime,.png,.jpg,.jpeg,.svg,.webp,.gif,.mp4,.webm,.mov,.m4v"
        class="sr-only"
        (change)="onReplaceFileSelected($event)"
      />

      @if (uploadFileName()) {
      <p class="m-0 mt-2 text-xs text-zinc-500 dark:text-zinc-400">Selected: {{ uploadFileName() }}</p>
      }
      @if (uploadState() === 'done' && uploadSuccessKey()) {
      <p class="m-0 mt-2 text-xs text-emerald-700 dark:text-emerald-300" role="status">
        Uploaded {{ uploadSuccessKey() }}
      </p>
      }
      @if (uploadState() === 'error' && uploadError()) {
      <p class="m-0 mt-2 text-xs text-red-700 dark:text-red-300" role="alert">{{ uploadError() }}</p>
      }
    </section>
  </header>

  <div class="min-h-0 flex-1 overflow-y-auto pt-5 scrollbar-transparent">
    @if (loadingState() === 'loading' || loadingState() === 'idle') {
    <p class="m-0 text-sm text-zinc-500 dark:text-zinc-400" role="status">Loading brand assets…</p>
    } @else if (loadingState() === 'error') {
    <div
      class="rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950/40"
      role="alert"
    >
      <p class="m-0 text-sm font-medium text-red-800 dark:text-red-200">Couldn’t load creatives</p>
      <p class="m-0 mt-1 text-sm text-red-700 dark:text-red-300">{{ loadError() }}</p>
      <button
        type="button"
        class="mt-3 rounded-lg border border-red-300 bg-white px-2.5 py-1.5 text-xs font-medium text-red-800 hover:bg-red-50 dark:border-red-800 dark:bg-red-950 dark:text-red-100 dark:hover:bg-red-900"
        (click)="loadLibrary()"
      >
        Try again
      </button>
    </div>
    } @else if (tabs().length === 0) {
    <p class="m-0 text-sm text-zinc-500 dark:text-zinc-400" role="status">
      No assets found in the S3 bucket.
    </p>
    } @else if (activeTab(); as tab) {
    @for (group of assetGroups(); track group.assetType) {
    <section class="mb-8 last:mb-0" [attr.aria-labelledby]="'creatives-group-' + group.assetType">
      <h3
        class="m-0 mb-3 text-sm font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400"
        [id]="'creatives-group-' + group.assetType"
      >
        {{ assetTypeHeading(group.assetType) }}
      </h3>

      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        @for (asset of group.assets; track asset.key) {
        <article
          class="flex flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950"
        >
          <div [class]="previewSurfaceClass(asset)">
            <img
              [src]="previewSrc(asset)"
              [alt]="previewAlt(asset)"
              loading="lazy"
              class="max-h-28 max-w-full object-contain"
            />
          </div>

          <div class="flex flex-1 flex-col gap-3 p-3">
            <div class="flex flex-wrap items-center gap-1.5">
              <p class="m-0 rounded bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-600 dark:bg-zinc-900 dark:text-zinc-300">
                {{ asset.variant }}
              </p>
              <p class="m-0 rounded bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-600 dark:bg-zinc-900 dark:text-zinc-300">
                {{ asset.size }}
              </p>
              <p
                class="m-0 rounded-full border border-zinc-300 bg-zinc-50 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-zinc-700 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-200"
              >
                {{ formatLabel(asset.format) }}
              </p>
            </div>

            <div
              class="inline-flex self-start rounded-md border border-zinc-200 bg-zinc-50 p-0.5 dark:border-zinc-700 dark:bg-zinc-900"
              role="group"
              [attr.aria-label]="'Background for ' + asset.key"
            >
              @for (mode of backgroundModes; track mode) {
              <button
                type="button"
                class="rounded px-2 py-1 text-[10px] font-medium transition-colors"
                [class]="
                  isCardBackgroundActive(asset, mode)
                    ? 'bg-white text-zinc-900 shadow-sm dark:bg-zinc-800 dark:text-zinc-100'
                    : 'text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-100'
                "
                (click)="setCardBackground(asset, mode)"
              >
                {{ backgroundModeLabel(mode) }}
              </button>
              }
            </div>

            <div class="mt-auto flex flex-wrap gap-2 pt-1">
              <button
                type="button"
                class="inline-flex items-center justify-center rounded-lg border border-zinc-300 bg-white px-2.5 py-1.5 text-xs font-medium text-zinc-800 hover:bg-zinc-50 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100 dark:hover:bg-zinc-800"
                (click)="copyUrl(asset)"
              >
                {{ isCopied(asset) ? 'Copied' : 'Copy URL' }}
              </button>
              <a
                class="inline-flex items-center justify-center rounded-lg border border-zinc-300 bg-white px-2.5 py-1.5 text-xs font-medium text-zinc-800 no-underline hover:bg-zinc-50 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100 dark:hover:bg-zinc-800"
                [href]="asset.url"
                [download]="downloadFileName(asset)"
                target="_blank"
                rel="noopener noreferrer"
              >
                Download
              </a>
              <button
                type="button"
                class="btn-shout btn-shout-xs btn-shout-secondary shrink-0"
                [disabled]="mutatingKey() !== null"
                (click)="startReplace(asset, replaceFileInput)"
              >
                {{ isReplacing(asset) ? 'Replacing…' : 'Replace' }}
              </button>
              @if (isDeletePending(asset)) {
              <p class="m-0 w-full text-xs text-zinc-600 dark:text-zinc-300">
                Remove this file from the library?
              </p>
              <button
                type="button"
                class="btn-shout btn-shout-xs btn-shout-alert shrink-0"
                [disabled]="mutatingKey() !== null"
                (click)="confirmDelete(asset)"
              >
                {{ isDeleting(asset) ? 'Removing…' : 'Remove' }}
              </button>
              <button
                type="button"
                class="btn-shout btn-shout-xs btn-shout-secondary shrink-0"
                [disabled]="mutatingKey() !== null"
                (click)="cancelDelete()"
              >
                Cancel
              </button>
              } @else {
              <button
                type="button"
                class="btn-shout btn-shout-xs btn-shout-alert shrink-0"
                [disabled]="mutatingKey() !== null"
                (click)="requestDelete(asset)"
              >
                Delete
              </button>
              }
            </div>

            @if (cardNoticeFor(asset); as notice) {
            <p
              class="m-0 text-xs"
              [class]="notice.tone === 'error' ? 'text-red-700 dark:text-red-300' : 'text-emerald-700 dark:text-emerald-300'"
              role="status"
            >
              {{ notice.message }}
            </p>
            }
          </div>
        </article>
        }
      </div>
    </section>
    }
    }
  </div>
</div>
